import { t } from '@exam-prep/i18n';
import type { MiniPlan } from '../model/plans-response';
import {
  coverageLines,
  isCovered,
  untilExam,
  meta,
  credits,
  groupPlans,
  bestKey,
  months,
  valueLine,
  packLine,
} from '../model/plans';

function Price({ p }: { p: MiniPlan }) {
  const offer = p.offer && p.listStars > p.stars;
  return (
    <span className="shrink-0 text-right font-display tabular-nums">
      {offer ? (
        <s className="block text-xs text-muted-foreground">
          <span className="sr-only">{t('miniapp.plans.was')} </span>
          {p.listStars} ⭐
        </s>
      ) : null}
      <span className={`block text-base font-bold${offer ? ' brand-accent-text' : ''}`}>
        {p.stars} ⭐
      </span>
    </span>
  );
}

function PlanRow({ p, best, value }: { p: MiniPlan; best: boolean; value: string | null }) {
  const cover = coverageLines(p);
  return (
    <div
      className={`plan-row flex items-center justify-between gap-3 rounded-xl border border-input p-3${best ? ' plan-row--best' : ''}${isCovered(p) ? ' plan-row--covered' : ''}`}
      data-plan={p.key}
      data-coverage={p.coverage?.status ?? undefined}
    >
      <span className="min-w-0">
        {best ? <span className="chip-best">{t('miniapp.plans.best_value')}</span> : null}
        {!best && untilExam(p) && p.scope === 'one' ? (
          <span className="chip-exam">{t('miniapp.plans.one_exam')}</span>
        ) : null}
        <span className="block text-sm font-semibold">{p.name}</span>
        {value ? <span className="plan-row__value block text-xs">{value}</span> : null}
        <span className="block text-xs text-muted-foreground">{meta(p)}</span>
        {cover.map((line) => (
          <span key={line} className="plan-row__cover block text-xs font-medium">
            {line}
          </span>
        ))}
      </span>
      {isCovered(p) ? null : <Price p={p} />}
    </div>
  );
}

function PackRow({ p, best, line }: { p: MiniPlan; best: boolean; line: string | null }) {
  return (
    <div
      className={`plan-row flex items-center justify-between gap-3 rounded-xl border border-input p-3${best ? ' plan-row--best' : ''}`}
      data-plan={p.key}
    >
      <span className="min-w-0">
        <span className="block text-sm font-semibold">
          {t('miniapp.plans.doubts', { n: credits(p) ?? 0 })}
          {best ? <span className="chip-best ml-2">{t('miniapp.plans.best_value')}</span> : null}
        </span>
        {line ? <span className="block text-xs text-muted-foreground">{line}</span> : null}
      </span>
      <Price p={p} />
    </div>
  );
}

/** The two groups, "Plans" and "AI doubt packs" (pure: rendered in tests). */
export function PlanGroups({
  plans,
  doubtCredits = 0,
}: {
  plans: readonly MiniPlan[];
  doubtCredits?: number;
}) {
  const { pro, packs } = groupPlans(plans);
  const best = bestKey(pro);
  const bestPack = bestKey(packs);
  const monthly = pro.find((p) => p.subscription || months(p) === 1) ?? null;
  return (
    <>
      {pro.length ? (
        <section className="space-y-2" aria-label={t('miniapp.plans.group_plans')}>
          <h3 className="text-sm font-bold">{t('miniapp.plans.group_plans')}</h3>
          {pro.map((p) => (
            <PlanRow key={p.key} p={p} best={p.key === best} value={valueLine(p, monthly)} />
          ))}
        </section>
      ) : null}
      {packs.length ? (
        <section className="space-y-2" aria-label={t('miniapp.plans.group_packs')}>
          <div className="space-y-1">
            <h3 className="text-sm font-bold">{t('miniapp.plans.group_packs')}</h3>
            <p className="text-xs text-muted-foreground">
              {t('miniapp.plans.packs_note')}
              {doubtCredits > 0 ? ` ${t('miniapp.plans.credits_left', { n: doubtCredits })}` : ''}
            </p>
          </div>
          {packs.map((p) => (
            <PackRow key={p.key} p={p} best={p.key === bestPack} line={packLine(p, packs)} />
          ))}
        </section>
      ) : null}
    </>
  );
}
